import { cloneDeep } from 'lodash';
import React from 'react';
import { assign } from 'xstate';
import { Grid, Chip, Typography, Box } from '@mui/material';
import { IntlShape } from 'react-intl';

import { theme } from '../../theme/theme';
import { MachineContext } from '../../MachineContext';
import { BaseComponent } from '../Component';
import {
  checkMessageObject,
  getStringValuesParam,
  translateWithFallback,
} from '../../util';

import { getBackgroundColor, MultipleDisplayEnum } from './MultipleDisplayEnum';

/*
  ! Single Display basic component
  it is actuallay a MultiDisplay but we wrap that in a dedicated component
  Reasons:
  1) it is easer to handle in player (SessionPlayer.tsx), e.g., to trigger a transition based on message length
  2) we can have a special render Action
*/
export interface SingleDisplayComponent extends BaseComponent {
  type: 'singleDisplay';
  props: SingleDisplayProps;
}
export interface SingleDisplayProps {
  message: string;
  topics?: string[];
  bubbleType: MultipleDisplayEnum;
}

export const SingleDisplay: React.FC<SingleDisplayProps> = ({
  message,
  topics,
  bubbleType,
}) => {
  return (
    <>
      <Box
        sx={{
          borderRadius: '6px',
          backgroundColor: getBackgroundColor(bubbleType),
        }}
      >
        <Typography
          sx={{
            display: 'block',
            padding: '15px',
            fontFamily: theme.typography.fontFamily,
          }}
        >
          {message}
        </Typography>
      </Box>
      <Grid container direction="row" justifyContent="flex-end">
        {topics !== undefined &&
          topics.map((topic, index) => {
            return (
              <Chip
                key={index}
                label={topic}
                sx={{
                  transform: 'translate(-10px, -10px)',
                  borderRadius: '2px',
                  backgroundColor: '#e97229',
                  color: '#ffffff',
                  width: 'max-content',
                  marginLeft: '10px',
                }}
              />
            );
          })}
      </Grid>
    </>
  );
};

/*
  ! renderSingleDisplayAction
  used in SessionPlayer
*/
export const renderSingleDisplayAction = (intl: IntlShape) => {
  return assign((context: MachineContext, _event, actionMetadata) => {
    const newContext = cloneDeep(context);
    newContext.widgetData = newContext.widgetData.filter(
      (element) => !element.temporary
    );

    const payload = actionMetadata.action.payload;
    const getValueFrom = payload.getValueFrom;
    const getTopicsValuesFrom = payload.getTopicsValuesFrom;

    const varsForString: Record<string, any> | undefined = getStringValuesParam(
      payload,
      context
    );

    // get ValueFrom may override message
    let message = payload.message
      ? translateWithFallback(intl, payload.message, varsForString)
      : '';
    if (getValueFrom) {
      message = context.userData[getValueFrom] ?? message;
    }
    // getTopicsValuesFrom generates list of topics
    let topics: string[] | undefined = undefined;
    if (
      getTopicsValuesFrom &&
      Array.isArray(getTopicsValuesFrom) &&
      getTopicsValuesFrom.length > 0
    ) {
      topics = getTopicsValuesFrom
        .map((topicvar: string) => {
          // topicvar may containt topics
          const topicvalue = context.userData[topicvar];
          if (topicvalue) {
            // check for type
            if (typeof topicvalue === 'string') {
              return topicvalue;
            } else {
              // if it is not a string, check for array or typed object
              if (Array.isArray(topicvalue)) {
                // value is an string array
                return topicvalue.map((value: any) =>
                  checkMessageObject(value, 'value', intl)
                );
              } else {
                // value is typed object (e.g. emotions)
                return topicvalue.map((typedobj: any) => {
                  return typedobj.value;
                });
              }
            }
          } else {
            return undefined;
          }
        })
        .flat()
        .filter((val) => val);
    }

    newContext.widgetData.push({
      type: 'singleDisplay',
      temporary: actionMetadata.action.temporary ?? false,
      props: {
        message: message,
        topics: topics,
        bubbleType: MultipleDisplayEnum.BUBBLE,
      },
    });
    return newContext;
  });
};
